import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { FilePlus2, FileText, TrendingUp, Building2, Calendar, ArrowRight, Search, Loader2, Pill } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS_STYLE = {
  pending: "bg-blue-50 text-blue-700",
  draft: "bg-slate-100 text-slate-600",
  in_progress: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-600",
};

function StatCard({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 font-medium">{label}</p>
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center"><Icon className="w-4 h-4 text-blue-600" /></div>
      </div>
      <p className="text-3xl font-bold text-slate-900 font-head mt-3">{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-1 truncate">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [rxStats, setRxStats] = useState(null);
  const [recentRx, setRecentRx] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/referrals/stats"),
      api.get("/referrals"),
      api.get("/templates"),
      api.get("/prescriptions/stats"),
      api.get("/prescriptions"),
    ]).then(([s, r, t, rs, rl]) => {
      setStats(s.data);
      setRecent(r.data.slice(0, 6));
      setTemplates(t.data.slice(0, 6));
      setRxStats(rs.data);
      setRecentRx(rl.data.slice(0, 4));
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Welcome, {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-slate-500 mt-1">Here's your referral activity at a glance.</p>
        </div>
        <Button data-testid="new-referral-btn" onClick={() => navigate("/referrals/new")} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">
          <FilePlus2 className="w-5 h-5 mr-1" /> New Referral
        </Button>
        <Button data-testid="new-prescription-btn" onClick={() => navigate("/prescriptions/new")} className="rounded-xl h-12 px-6 bg-slate-900 hover:bg-slate-800">
          <Pill className="w-5 h-5 mr-1" /> New Prescription
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        <StatCard icon={Calendar} label="Referrals this month" value={stats?.this_month ?? 0} />
        <StatCard icon={Pill} label="Prescriptions this month" value={rxStats?.this_month ?? 0} />
        <StatCard icon={FileText} label="Total referrals" value={stats?.total ?? 0} />
        <StatCard icon={Building2} label="Total prescriptions" value={rxStats?.total ?? 0} />
      </div>

      <div className="mt-8 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="dashboard-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && navigate(`/history?q=${search}`)} placeholder="Search referrals by patient or number…" className="pl-10 h-12 rounded-xl" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-slate-900 font-head">Recent Referrals</h2>
            <Link to="/history" className="text-sm text-blue-600 font-medium hover:underline flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
            {recent.length === 0 && <div className="p-10 text-center text-slate-400"><FileText className="w-8 h-8 mx-auto mb-2" />No referrals yet. Create your first one!</div>}
            {recent.map((r) => (
              <button key={r.id} data-testid={`recent-referral-${r.id}`} onClick={() => navigate(`/referrals/${r.id}`)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left transition-colors">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{r.patient?.first_name} {r.patient?.last_name}</p>
                  <p className="text-sm text-slate-500 truncate">{r.studies?.map((s) => s.name).join(", ") || "—"}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status] || "bg-slate-100"}`}>{r.status}</span>
                  <span className="text-xs text-slate-400 hidden sm:block">{r.referral_number}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-slate-900 font-head">Saved Templates</h2>
            <Link to="/templates" className="text-sm text-blue-600 font-medium hover:underline">Manage</Link>
          </div>
          <div className="space-y-3">
            {templates.length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-slate-400 text-sm">No templates</div>}
            {templates.map((t) => (
              <button key={t.id} data-testid={`template-quick-${t.id}`} onClick={() => navigate(`/referrals/new?template=${t.id}`)} className="w-full bg-white rounded-2xl border border-slate-200 p-4 text-left hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
                <p className="text-xs text-slate-500 mt-1">{t.studies?.map((s) => s.name).join(", ")}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
